import { useCallback, useEffect, useRef, useState } from 'react';
import { autoplannerAPI } from '../../services/api';

/**
 * useValidationFlow
 * Encapsulates the validation process (initial + background) and exposes UI state.
 * Keeps behavior aligned with Step3Validation original logic.
 */
export default function useValidationFlow(params) {
  const {
    taskId,
    autoplannerTask,
    setAutoplannerTask,
    trainingGroupId,
    selectedSessions,
    startDate,
    endDate,
    onAfterValidate, // optional callback after successful combine
  } = params || {};

  const [validationData, setValidationData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [backgroundValidating, setBackgroundValidating] = useState(false);
  const [lastValidationAt, setLastValidationAt] = useState(null);
  const validationRunIdRef = useRef(0);

  const combineResults = useCallback((availabilityResult, feasibilityResult, seatBalanceResult) => {
    const combinedResult = {
      availability_check: availabilityResult,
      scheduling_feasibility: feasibilityResult,
      seat_capacity_balance: seatBalanceResult,
      valid: availabilityResult?.complete && feasibilityResult?.feasible && seatBalanceResult?.balanced,
      issues: [],
      warnings: [],
      recommendations: [],
    };

    const missingRowersCount = availabilityResult?.missing_users?.length || 0;
    if (missingRowersCount > 0) {
      combinedResult.issues.push(
        `${missingRowersCount} rower${missingRowersCount === 1 ? '' : 's'} missing availability`
      );
    }
    const missingCoachesCount = availabilityResult?.missing_coaches?.length || 0;
    if (missingCoachesCount > 0) {
      combinedResult.warnings.push(
        `${missingCoachesCount} coach${missingCoachesCount === 1 ? '' : 'es'} missing availability (auto-ignored unless explicitly included)`
      );
    }
    if (!feasibilityResult?.feasible) {
      combinedResult.issues.push('Scheduling is not feasible with current availability');
    }
    if (!seatBalanceResult?.balanced) {
      const issuesCount = seatBalanceResult?.sessions_with_issues || 0;
      const warningsCount = seatBalanceResult?.sessions_with_warnings || 0;
      if (issuesCount > 0) {
        combinedResult.issues.push(
          `${issuesCount} session${issuesCount === 1 ? '' : 's'} have insufficient rowers for available seats`
        );
      }
      if (warningsCount > 0) {
        combinedResult.issues.push(
          `${warningsCount} session${warningsCount === 1 ? '' : 's'} have more rowers than available seats`
        );
      }
    }

    if (feasibilityResult?.recommendations) {
      combinedResult.recommendations.push(...feasibilityResult.recommendations);
    }
    if (seatBalanceResult?.recommendations) {
      combinedResult.recommendations.push(...seatBalanceResult.recommendations);
    }

    return combinedResult;
  }, []);

  const validateNow = useCallback(async () => {
    try {
      setIsProcessing(true);
      setProgress(0);
      setCurrentStep('Checking availability...');

      if (!selectedSessions || selectedSessions.length === 0) throw new Error('No sessions selected for validation');
      if (!startDate || !endDate) throw new Error('Missing start or end date');

      let currentTask = autoplannerTask;
      if (!currentTask && taskId) {
        currentTask = await autoplannerAPI.getAutoplannerTask(taskId);
        setAutoplannerTask && setAutoplannerTask(currentTask);
      }
      if (!currentTask && !taskId) {
        setIsProcessing(false);
        return;
      }

      if (
        !trainingGroupId &&
        !currentTask?.training_group_id &&
        !currentTask?.training_group?.id
      ) {
        throw new Error('Missing training group ID');
      }

      setProgress(25);
      const availabilityResult = await autoplannerAPI.validateAvailability(currentTask.id);
      setProgress(50);
      setCurrentStep('Analyzing scheduling feasibility...');
      const feasibilityResult = await autoplannerAPI.validateSchedulingFeasibility(currentTask.id);
      setProgress(75);
      setCurrentStep('Analyzing seat capacity balance...');
      const seatBalanceResult = await autoplannerAPI.validateSeatCapacityBalance(currentTask.id);
      setProgress(90);
      setCurrentStep('Combining results...');

      const combined = combineResults(availabilityResult, feasibilityResult, seatBalanceResult);
      setValidationData(combined);
      setProgress(100);
      setCurrentStep('Validation complete');
      setIsProcessing(false);
      setLastValidationAt(Date.now());
      if (typeof onAfterValidate === 'function') onAfterValidate(combined);
    } catch (error) {
      setValidationData({
        valid: false,
        error: error.message,
        issues: [`Validation failed: ${error.message}`],
        availability_check: null,
        scheduling_feasibility: null,
      });
      setIsProcessing(false);
    }
  }, [trainingGroupId, selectedSessions, startDate, endDate, autoplannerTask, taskId, setAutoplannerTask, combineResults, onAfterValidate]);

  const triggerBackgroundValidation = useCallback(async () => {
    try {
      if (backgroundValidating) return;
      let currentTask = autoplannerTask;
      if (!currentTask && taskId) {
        currentTask = await autoplannerAPI.getAutoplannerTask(taskId);
        setAutoplannerTask && setAutoplannerTask(currentTask);
      } else if (!currentTask && !taskId) {
        setIsProcessing(false);
        return;
      }
      if (!currentTask) return;

      setBackgroundValidating(true);
      const runId = ++validationRunIdRef.current;

      const availabilityResult = await autoplannerAPI.validateAvailability(currentTask.id);
      const feasibilityResult = await autoplannerAPI.validateSchedulingFeasibility(currentTask.id);
      const seatBalanceResult = await autoplannerAPI.validateSeatCapacityBalance(currentTask.id);

      if (validationRunIdRef.current === runId) {
        const combined = combineResults(availabilityResult, feasibilityResult, seatBalanceResult);
        setValidationData(combined);
        setLastValidationAt(Date.now());
        if (typeof onAfterValidate === 'function') onAfterValidate(combined);
      }
    } catch (e) {
      // swallow background errors
    } finally {
      setBackgroundValidating(false);
    }
  }, [backgroundValidating, autoplannerTask, taskId, setAutoplannerTask, combineResults, onAfterValidate]);

  return {
    validationData,
    setValidationData,
    isProcessing,
    progress,
    currentStep,
    backgroundValidating,
    lastValidationAt,
    validateNow,
    triggerBackgroundValidation,
  };
}



